import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChevronDown,
  Landmark,
  ShieldCheck,
  Check,
  MapPin,
  Wallet,
  Users,
  Scale,
  Martini,
} from "lucide-react";
import Reveal from "../components/Reveal";
import Placeholder from "../components/Placeholder";
import VenueBlock from "../components/VenueBlock";
import VenueCard from "../components/VenueCard";
import HeroVenues from "../components/HeroVenues";
import { venues } from "../mock/venues";
import {
  introHeading,
  introParagraphs,
  historySection,
  legalSection,
  venuesIntro,
  otherVenuesSection,
  precincts,
  stripClubsSection,
  expectSection,
  choosingSection,
  faqSection,
  employmentSection,
  closingSection,
  guideImages,
} from "../mock/guide";

const SectionHead = ({ eyebrow, title, sub, center }) => (
  <Reveal>
    <div className={center ? "text-center" : ""}>
      <span className="eyebrow mb-4">{eyebrow}</span>
      <h2 className="wiki-h1">{title}</h2>
      <div className={`section-divider mt-6 ${center ? "" : "ml-0"}`} />
      {sub ? (
        <p
          className={`mt-7 max-w-3xl leading-relaxed text-muted-foreground ${
            center ? "mx-auto" : ""
          }`}
        >
          {sub}
        </p>
      ) : null}
    </div>
  </Reveal>
);

const Home = () => {
  return (
    <div data-testid="guide-page">
      {/* ------------------------------- HERO ------------------------------- */}
      <section className="relative flex items-center overflow-hidden border-b border-border bg-background">
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-16 text-center sm:px-6 lg:px-8">
          <Reveal delay={120}>
            <h1 className="font-serif text-4xl tracking-tight text-foreground sm:text-6xl lg:text-7xl">
              MELBOURNE BROTHELS
              <span className="mt-3 block text-2xl font-light tracking-[0.24em] text-accent sm:text-3xl lg:text-4xl">
                GUIDE 2026
              </span>
            </h1>
            <p className="mx-auto mt-7 max-w-3xl text-base leading-relaxed text-muted-foreground md:text-lg">
              Models, prices, rooms and locations for every venue, the most
              comprehensive guide to Melbourne brothels.
            </p>
          </Reveal>
          <Reveal delay={280}>
            <div className="mt-12">
              <HeroVenues />
            </div>
          </Reveal>
        </div>
      </section>

      {/* --------------------------- INTRODUCTION --------------------------- */}
      <section id="introduction" className="scroll-mt-32 py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Introduction" title={introHeading} />
          <div className="mt-12">
            <details
              data-testid="intro-accordion"
              className="group max-w-4xl overflow-hidden rounded-xl border border-border bg-card"
            >
              <summary
                data-testid="intro-accordion-toggle"
                className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-5 text-left transition-colors hover:bg-muted/60 [&::-webkit-details-marker]:hidden"
              >
                <span className="font-serif text-base text-foreground md:text-lg">
                  Read the introduction: Melbourne's adult scene, the law and this guide
                </span>
                <ChevronDown className="h-5 w-5 flex-shrink-0 text-primary transition-transform duration-300 group-open:rotate-180" />
              </summary>
              <div className="space-y-6 border-t border-border px-6 pb-7 pt-6">
                {introParagraphs.map((p, i) => (
                  <p
                    key={i}
                    className={
                      i === 0
                        ? "text-lg leading-relaxed text-foreground/90"
                        : "leading-relaxed text-muted-foreground"
                    }
                  >
                    {p}
                  </p>
                ))}
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* ------------------------------ VENUES ------------------------------ */}
      <section id="venues" className="relative scroll-mt-32 bg-card py-20 sm:py-24">
        <div className="hairline absolute inset-x-0 top-0" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            eyebrow="The Venues"
            title={venuesIntro.title}
            center
            sub={venuesIntro.paragraphs[0]}
          />
          <div className="mx-auto mt-8 max-w-3xl space-y-5 text-center">
            {venuesIntro.paragraphs.slice(1).map((p, i) => (
              <Reveal key={i} delay={i * 70}>
                <p className="leading-relaxed text-muted-foreground">{p}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-14 grid gap-10">
            {venues.map((v) => (
              <VenueCard key={v.slug} venue={v} delay={0} />
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------- ALL VENUES IN FULL ------------------------ */}
      {venues.map((v, i) => (
        <VenueBlock key={v.slug} venue={v} index={i} />
      ))}

      {/* ------------------------------ HISTORY ----------------------------- */}
      <section id="history" className="relative scroll-mt-32 bg-card py-20 sm:py-24">
        <div className="hairline absolute inset-x-0 top-0" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="History" title={historySection.title} center />
          <div className="mx-auto mt-10 max-w-3xl space-y-5 text-center">
            {historySection.intro.map((p, i) => (
              <Reveal key={i} delay={i * 70}>
                <p className="leading-relaxed text-muted-foreground">{p}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-14 grid gap-8 lg:grid-cols-2">
            {historySection.blocks.map((b, i) => (
              <Reveal key={b.title} delay={i * 110}>
                <article className="luxury-card h-full p-8">
                  <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
                    <Landmark className="h-5 w-5 text-primary" />
                  </span>
                  <h3 className="font-serif text-xl text-foreground sm:text-2xl">{b.title}</h3>
                  <div className="mt-5 space-y-4">
                    {b.paragraphs.map((p, j) => (
                      <p key={j} className="text-sm leading-relaxed text-muted-foreground">
                        {p}
                      </p>
                    ))}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal delay={120}>
            <figure className="mx-auto mt-14 max-w-3xl">
              <Placeholder src={guideImages.green} label="Melbourne Brothels Guide" ratio="16/9" />
              <figcaption className="mt-3 text-center text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Melbourne Brothels Guide
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* ------------------------------- LEGAL ------------------------------ */}
      <section id="legal" className="scroll-mt-32 py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="The Law" title={legalSection.title} sub={legalSection.intro} />
          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            <Reveal>
              <div className="luxury-card h-full p-8">
                <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
                  <Scale className="h-5 w-5 text-primary" />
                </span>
                <h3 className="font-serif text-xl text-foreground sm:text-2xl">
                  The 2022 and 2023 Reforms
                </h3>
                <div className="mt-5 space-y-4">
                  {legalSection.paragraphs.map((p, i) => (
                    <p key={i} className="text-sm leading-relaxed text-muted-foreground">
                      {p}
                    </p>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={110}>
              <div className="luxury-card h-full p-8">
                <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
                  <ShieldCheck className="h-5 w-5 text-primary" />
                </span>
                <h3 className="font-serif text-xl text-foreground sm:text-2xl">
                  What Decriminalisation Does Not Mean
                </h3>
                <ul className="mt-5 space-y-3.5">
                  {legalSection.points.map((p) => (
                    <li key={p} className="flex items-start gap-3">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                      <span className="text-sm leading-relaxed text-muted-foreground">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
          <Reveal delay={140}>
            <p className="mx-auto mt-10 max-w-3xl border-l-2 border-primary/60 bg-muted/40 px-6 py-5 leading-relaxed text-muted-foreground">
              {legalSection.note}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---------------------- OTHER ESTABLISHMENTS ------------------------ */}
      <section id="precincts" className="relative scroll-mt-32 bg-card py-20 sm:py-24">
        <div className="hairline absolute inset-x-0 top-0" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Also in Melbourne" title={otherVenuesSection.title} center />
          <div className="mx-auto mt-10 max-w-3xl space-y-5 text-center">
            {otherVenuesSection.intro.map((p, i) => (
              <Reveal key={i} delay={i * 70}>
                <p className="leading-relaxed text-muted-foreground">{p}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2">
            {precincts.map((p, i) => (
              <Reveal key={p.title} delay={(i % 2) * 100}>
                <article className="luxury-card group h-full p-8">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-serif text-xl text-foreground transition-colors group-hover:text-primary">
                      {p.title}
                    </h3>
                    <MapPin className="h-4 w-4 flex-shrink-0 text-primary/70" />
                  </div>
                  <p className="mt-4 text-sm font-medium text-foreground">{p.text}</p>
                  <div className="mt-4 space-y-3">
                    {p.paragraphs.map((t, j) => (
                      <p key={j} className="text-sm leading-relaxed text-muted-foreground">
                        {t}
                      </p>
                    ))}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal delay={120}>
            <figure className="mx-auto mt-14 max-w-3xl">
              <Placeholder src={guideImages.pink} label="Melbourne Brothels Guide" ratio="16/9" />
              <figcaption className="mt-3 text-center text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Melbourne Brothels Guide
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* ---------------------------- STRIP CLUBS --------------------------- */}
      <section id="strip-clubs" className="scroll-mt-32 py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            eyebrow="Not the Same Thing"
            title={stripClubsSection.title}
            sub={stripClubsSection.intro[0]}
          />
          <div className="mt-10 max-w-3xl space-y-5">
            {stripClubsSection.intro.slice(1).map((p, i) => (
              <Reveal key={i} delay={i * 70}>
                <p className="leading-relaxed text-muted-foreground">{p}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2">
            {stripClubsSection.clubs.map((c, i) => (
              <Reveal key={c.title} delay={i * 100}>
                <article className="luxury-card group h-full p-8">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-serif text-xl text-foreground transition-colors group-hover:text-primary">
                      {c.title}
                    </h3>
                    <Martini className="h-4 w-4 flex-shrink-0 text-primary/70" />
                  </div>
                  <p className="mt-4 text-sm font-medium text-foreground">{c.text}</p>
                  <div className="mt-4 space-y-3">
                    {c.paragraphs.map((t, j) => (
                      <p key={j} className="text-sm leading-relaxed text-muted-foreground">
                        {t}
                      </p>
                    ))}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------------- WHAT TO EXPECT -------------------------- */}
      <section id="what-to-expect" className="relative scroll-mt-32 bg-card py-20 sm:py-24">
        <div className="hairline absolute inset-x-0 top-0" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Visiting" title={expectSection.title} sub={expectSection.intro} />

          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {expectSection.steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 90}>
                <div className="luxury-card h-full p-7">
                  <span className="font-serif text-3xl text-primary/50">0{i + 1}</span>
                  <h3 className="mt-3 font-serif text-lg text-foreground">{s.title}</h3>
                  <div className="mt-4 space-y-3">
                    {s.paragraphs.map((p, j) => (
                      <p key={j} className="text-sm leading-relaxed text-muted-foreground">
                        {p}
                      </p>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 grid gap-8 lg:grid-cols-[1fr_1.1fr]">
            <Reveal>
              <div className="luxury-card h-full p-8">
                <h3 className="font-serif text-2xl text-foreground">
                  {expectSection.etiquetteTitle}
                </h3>
                <p className="mt-3 text-sm text-muted-foreground">
                  {expectSection.etiquetteIntro}
                </p>
                <ul className="mt-6 space-y-3.5">
                  {expectSection.etiquette.map((e) => (
                    <li key={e} className="flex items-start gap-3">
                      <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                      <span className="text-sm leading-relaxed text-muted-foreground">{e}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="grid h-full gap-6 sm:grid-cols-2">
                {expectSection.etiquetteExtra.map((x) => (
                  <div key={x.title} className="luxury-card p-7">
                    <h4 className="mb-5 font-serif text-lg text-foreground">{x.title}</h4>
                    <div className="space-y-3">
                      {x.paragraphs.map((p, j) => (
                        <p key={j} className="text-sm leading-relaxed text-muted-foreground">
                          {p}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal delay={120}>
            <figure className="mx-auto mt-16 max-w-3xl">
              <Placeholder src={guideImages.black} label="Melbourne Brothels Guide" ratio="16/9" />
              <figcaption className="mt-3 text-center text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Melbourne Brothels Guide
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* --------------------------- CHOOSING VENUE -------------------------- */}
      <section id="choosing" className="scroll-mt-32 py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Guidance" title={choosingSection.title} sub={choosingSection.intro} />

          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {choosingSection.points.map((p, i) => (
              <Reveal key={p} delay={i * 90}>
                <div className="luxury-card h-full p-7">
                  <span className="font-serif text-3xl text-primary/50">0{i + 1}</span>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-10 grid gap-8 lg:grid-cols-2">
            <Reveal>
              <div className="luxury-card h-full p-8">
                <h3 className="mb-6 flex items-center gap-3 font-serif text-xl text-foreground">
                  <ShieldCheck className="h-5 w-5 text-primary" /> {choosingSection.safetyTitle}
                </h3>
                <ul className="space-y-3.5">
                  {choosingSection.safety.map((s) => (
                    <li key={s} className="flex items-start gap-3">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                      <span className="text-sm leading-relaxed text-muted-foreground">{s}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-6 border-l-2 border-primary/60 bg-muted/40 px-5 py-4 text-sm italic text-muted-foreground">
                  {choosingSection.safetyNote}
                </p>
              </div>
            </Reveal>
            <Reveal delay={110}>
              <div className="luxury-card h-full p-8">
                <h3 className="mb-6 flex items-center gap-3 font-serif text-xl text-foreground">
                  <Wallet className="h-5 w-5 text-primary" /> {choosingSection.paymentTitle}
                </h3>
                <ul className="space-y-3.5">
                  {choosingSection.payment.map((s) => (
                    <li key={s} className="flex items-start gap-3">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                      <span className="text-sm leading-relaxed text-muted-foreground">{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* -------------------------------- FAQS ------------------------------ */}
      <section id="faqs" className="relative scroll-mt-32 bg-card py-20 sm:py-24">
        <div className="hairline absolute inset-x-0 top-0" />
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="FAQs" title={faqSection.title} center />
          <div className="mt-12 space-y-4">
            {faqSection.items.map((item, i) => (
              <Reveal key={item.q} delay={i * 50}>
                <details
                  data-testid="faq-item"
                  className="group overflow-hidden rounded-xl border border-border bg-background"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-5 text-left transition-colors hover:bg-muted/60 [&::-webkit-details-marker]:hidden">
                    <span className="font-serif text-base text-foreground md:text-lg">
                      {item.q}
                    </span>
                    <ChevronDown className="h-5 w-5 flex-shrink-0 text-primary transition-transform duration-300 group-open:rotate-180" />
                  </summary>
                  <div className="space-y-4 border-t border-border px-6 pb-6 pt-5">
                    {item.a.map((p, j) => (
                      <p key={j} className="leading-relaxed text-muted-foreground">
                        {p}
                      </p>
                    ))}
                  </div>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------------------- EMPLOYMENT ---------------------------- */}
      <section id="employment" className="scroll-mt-32 py-20 sm:py-24">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="luxury-card p-9 text-center sm:p-14">
              <span className="mx-auto mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </span>
              <h2 className="font-serif text-3xl text-foreground sm:text-4xl">
                {employmentSection.title}
              </h2>
              <div className="section-divider my-6" />
              <div className="mx-auto max-w-2xl space-y-4">
                {employmentSection.paragraphs.map((p, i) => (
                  <p key={i} className="leading-relaxed text-muted-foreground">
                    {p}
                  </p>
                ))}
              </div>
              <Link to="/employment" className="btn-pink-glow mt-9 inline-flex">
                View Melbourne Adult Jobs Page <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ------------------------------ CLOSING ----------------------------- */}
      <section id="closing" className="relative scroll-mt-32 bg-card py-20 sm:py-24">
        <div className="hairline absolute inset-x-0 top-0" />
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="In Summary" title={closingSection.title} />
          <div className="mt-10 space-y-5">
            {closingSection.paragraphs.map((p, i) => (
              <Reveal key={i} delay={i * 50}>
                <p className="leading-relaxed text-muted-foreground">{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
